/**
 * Conexiones WebRTC en vivo "en espera" después de que su player se destruye
 * (cambio de cámara/página en el mosaico, cambio de inputs).
 *
 * Mientras la RTCPeerConnection sigue abierta, go2rtc la cuenta como consumer
 * y no corta la sesión RTSP contra el equipo. Si en LINGER_MS se vuelve a pedir
 * la misma cámara+canal+calidad, el player nuevo adopta la conexión y el video
 * aparece al instante, sin abrir otra sesión contra el DVR (que muchas veces
 * todavía cuenta la recién cerrada y rechaza la nueva).
 *
 * Tope de MAX_PARKED: recorrer con las flechas muchos canales del mismo DVR
 * dejaría una sesión abierta por canal; al pasar el tope se cierra la más
 * vieja. Solo vivo: una grabación adoptada seguiría desde donde iba, no desde
 * la fecha pedida.
 */
const LINGER_MS = 15_000;
const MAX_PARKED = 3;

export interface ParkedPeer {
  pc: RTCPeerConnection;
  stream: MediaStream;
}

interface Entry extends ParkedPeer {
  timer: ReturnType<typeof setTimeout>;
}

const parked = new Map<string, Entry>();

function closeEntry(entry: Entry): void {
  clearTimeout(entry.timer);
  entry.pc.onconnectionstatechange = null;
  try {
    entry.pc.close();
  } catch {
    /* ignore */
  }
}

function drop(key: string): void {
  const entry = parked.get(key);
  if (!entry) return;
  parked.delete(key);
  closeEntry(entry);
}

function isHealthy({ pc, stream }: ParkedPeer): boolean {
  return (
    pc.connectionState === 'connected' &&
    stream.getVideoTracks().some((t) => t.readyState === 'live')
  );
}

export const livePeerPool = {
  /** Deja la conexión en espera. Si no está sana la cierra directamente. */
  park(key: string, peer: ParkedPeer): void {
    drop(key);
    // Los handlers del player que la soltó ya no aplican.
    peer.pc.ontrack = null;
    peer.pc.oniceconnectionstatechange = null;
    if (!isHealthy(peer)) {
      try {
        peer.pc.close();
      } catch {
        /* ignore */
      }
      return;
    }
    while (parked.size >= MAX_PARKED) {
      drop(parked.keys().next().value as string);
    }
    peer.pc.onconnectionstatechange = () => {
      if (
        ['failed', 'closed', 'disconnected'].includes(peer.pc.connectionState)
      ) {
        drop(key);
      }
    };
    parked.set(key, { ...peer, timer: setTimeout(() => drop(key), LINGER_MS) });
  },

  has(key: string): boolean {
    return parked.has(key);
  },

  /** Saca la conexión en espera para reusarla, si sigue sana. */
  take(key: string): ParkedPeer | null {
    const entry = parked.get(key);
    if (!entry) return null;
    parked.delete(key);
    clearTimeout(entry.timer);
    entry.pc.onconnectionstatechange = null;
    if (!isHealthy(entry)) {
      closeEntry(entry);
      return null;
    }
    return { pc: entry.pc, stream: entry.stream };
  },
};
